import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {STATUS_MODEL} from '../../../../../enums/statusEnum';
import { STATUS_ICON_MODEL } from '../Model/Model';
import routes from '../../../../../Routes/routes';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { chipStatusClassColor, chipStatusIconClassColor } from '../../../../../enums/statusColorEnum';
import {
    DocumentationUserUpdateStatusById, DocumentationVehicleUpdateStatusById,
    GetDocumentationUserByUserId, GetDocumentationVehicleByVehicleId
} from '../../../../../reduxToolkit/DocumentationApproval/DocumentationApprovalApi';
import {errorMsg} from '../../../../../Apis/functions';
import { PROFILE_MODEL } from '../../../../DocumentationApprovalFlow/Model/Model';
import { useDispatch, useSelector } from 'react-redux';
import { permissionObj } from '../../../../../Helpers/permission';
import {toast} from 'react-toastify';
import {getToastMessageByStatus} from '../../../ApprovalTracker/Model/Model';
import {USER_TYPE_MODEL} from '../../../../../enums/userTypeEnum';

/**
 * @Email : sgarcia@ibl.mx
 * @Author: Susana Garcia Rodriguez
 * @jobTittle: Frontend
 */
export const RequestDetailsApp = ({userTypeId}) => {
    const { id, profile } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [documentationApproval, setDocumentationApproval] = useState('');

    // State to control visibility of approval modal
    const [showApprove, setShowApprove] = useState(false);
    // State to control visibility of denial modal
    const [showDenied, setShowDenied] = useState(false);
    // State to control visibility of issue/comment modal
    const [showIssueComment, setShowIssueComment] = useState(false);
    // State that indicates if data was modified and must be refreshed
    const [modified, setModified] = useState(false);
    // Holds the selected row for any action (approve/deny/comment)
    const [row, setRow] = useState(null);
    // Last refresh nonce already handled, so the initial load is not duplicated
    const handledRefreshRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    let { state } = location;
    const currentTab = state?.currentTab;
    const currentSubView = state?.currentSubView;
    const userTypeFinal = state?.userType ?? userTypeId;

    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    // Read-only users open the request as details, so the approval actions stay hidden
    const canManageRequest = DEV
        || permission?.includes(userTypeFinal === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE
            ? permissionObj?.WEB_CONTRACTOR_REQUEST_MANAGE
            : permissionObj?.WEB_SUPPLIER_REQUEST_MANAGE);

    // Gets icon and colors based on the overall approval status
    const statusData = STATUS_ICON_MODEL[documentationApproval?.approvalStatusId];
    const statusId = documentationApproval?.approvalStatusId;
    const chipClass = chipStatusClassColor[statusId];
    const iconClass = chipStatusIconClassColor[statusId];

    // Generic modal configuration for approve/deny/request changes actions
    const genericCommentModals = [
        {
            show: showApprove,
            setter: setShowApprove,
            title: 'approve_request',
            setRow: setRow,
            row: row,
            status: STATUS_MODEL.APPROVED,
            requiredComment: false,
            onClick: (reason) => {
                let endPoint = profile === PROFILE_MODEL.VEHICLE ? DocumentationVehicleUpdateStatusById: DocumentationUserUpdateStatusById;
                const body = {
                    id: row?.id,
                    statusId: STATUS_MODEL.APPROVED,
                    reason: reason,
                    userTypeId: userTypeFinal
                };
                return dispatch(endPoint(body));
            }
        },
        {
            show: showDenied,
            setter: setShowDenied,
            title: 'decline_comment',
            setRow: setRow,
            row: row,
            status: STATUS_MODEL.DENIED,
            requiredComment: true,
            onClick: (reason) => {
                let endPoint = profile === PROFILE_MODEL.VEHICLE ? DocumentationVehicleUpdateStatusById: DocumentationUserUpdateStatusById;
                const body = {
                    id: row?.id,
                    statusId: STATUS_MODEL.DENIED,
                    reason: reason,
                    userTypeId: userTypeFinal
                };
                return dispatch(endPoint(body));
            }
        },
        {
            show: showIssueComment,
            setter: setShowIssueComment,
            title: 'request_changes',
            setRow: setRow,
            row: row,
            status: STATUS_MODEL.CHANGES_REQUESTED,
            requiredComment: true,
            onClick: (reason) => {
                let endPoint = profile === PROFILE_MODEL.VEHICLE ? DocumentationVehicleUpdateStatusById : DocumentationUserUpdateStatusById;
                const body = {
                    id: row?.id,
                    statusId: STATUS_MODEL.CHANGES_REQUESTED,
                    reason: reason,
                    userTypeId: userTypeFinal
                };
                return dispatch(endPoint(body));
            }
        },
    ];

    // Refreshes data after modifications
    useEffect(() => {
        if (modified) {
            getData();
            setModified(false);
        }
    }, [modified]);

    // Loads initial data on component mount
    useEffect(() => {
        getData();
    }, []);

    /**
     * Refreshes when a caller lands on this view asking for fresh data.
     *
     * The initial load only runs on mount, and React Router keeps this component
     * mounted when the destination resolves to the same route, so arriving from a
     * notification while already standing here would keep showing stale data.
     * `refreshedAt` carries a nonce per navigation and is remembered, so the
     * initial load is not duplicated.
     */
    useEffect(() => {
        const refreshedAt = state?.refreshedAt;
        if (!refreshedAt || handledRefreshRef.current === refreshedAt) return;
        handledRefreshRef.current = refreshedAt;
        setModified(true);
    }, [state?.refreshedAt]);

    // Fetches the request details from the API (placeholder)
    const getData = () => {
        if (profile === PROFILE_MODEL.VEHICLE) {
            apiDocumentationByVehicleId(id);
        } else {
            apiDocumentationByUserId(id);
        }
    };

    /**
     * Navigates back to the contractors list,
     * preserving the current tab and subview state.
     */
    const handleOnBack = () => {
        const url = userTypeFinal=== USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE ? routes.contractor.list : routes.suppliers.main;
        navigate(url, {
            state: {
                currentTab: currentTab,
                currentSubView: currentSubView,
            },
        });
    };

    /**
     * Fetches documentation for a user by their ID and stores it in state.
     * Shows an error message if the operation fails.
     *
     * @param {string|number} id - The user ID
     */
    const apiDocumentationByUserId = (id) => {

        dispatch(GetDocumentationUserByUserId({id, userTypeId: userTypeFinal})).then(({ payload: { data: { data, success, code } } }) => {
            if (success) {
                setDocumentationApproval(data);
            } else errorMsg(code)
        })
    }

    /**
     * Fetches documentation for a vehicle by its ID and stores it in state.
     * Shows an error message if the operation fails.
     *
     * @param {string|number} id - The vehicle ID
     */
    const apiDocumentationByVehicleId = (id) => {
        dispatch(GetDocumentationVehicleByVehicleId({id, userTypeId: userTypeFinal})).then(({ payload: { data: { data, success, code } } }) => {
            if (success) {
                setDocumentationApproval(data);
            } else errorMsg(code)
        })
    }

    /**
     * Handles the click on approval/rejection buttons.
     * Calls the `onClick` function with the comment, and updates state or shows a toast.
     *
     * @param {Function} onClick - Function that performs the action (approve, deny, etc.)
     * @param {string} comment - Optional comment to send with the action
     * @param {string} status - Approval status (APPROVED, DENIED, CHANGES_REQUESTED)
     */
    const handleOnClick = (onClick, comment, status) => {
        onClick(comment).then(({ payload: { data: { success, code } } }) => {
            if (success) {
                setModified(true);
                toast.success(t(getToastMessageByStatus(status)));
            } else {
                errorMsg(code);
            }
        });
    }

    return {
        t,
        genericCommentModals,
        modified,
        setModified,
        documentationApproval,
        setShowApprove,
        setShowDenied,
        setShowIssueComment,
        setRow,
        row,
        handleOnBack,
        chipClass,
        iconClass,
        statusData,
        profile,
        handleOnClick,
        canManageRequest
    };
};
